import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between border-b px-6 py-4 shrink-0" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center gap-1 mt-1 text-xs text-muted-foreground/70"
}
const _hoisted_3 = { class: "font-mono text-[11px]" }
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = { class: "flex-1 overflow-hidden flex flex-col px-6 py-4" }
const _hoisted_6 = { class: "flex items-end gap-4 mb-4 shrink-0" }
const _hoisted_7 = { class: "flex-1 space-y-1" }
const _hoisted_8 = { class: "w-48 space-y-1" }
const _hoisted_9 = { class: "flex items-center gap-2 pb-1" }
const _hoisted_10 = { class: "flex-1 border rounded-md overflow-hidden" }

import { ref, watch, computed, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/components/ui/toast'
import { useDocumentsStore } from '../store'
import { draftKey, saveDraft, loadDraft, clearDraft, useMinimizedDocuments } from '@/composables/useMinimizedDocuments'
import { MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import type { Document, DocumentEntityType, DocumentType } from '@/types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Loader2, Minus, Copy, Workflow, Square, Code2 } from 'lucide-vue-next'
import { DIAGRAM_TYPES } from './diagramTypes'


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentFormDialog',
  props: {
    open: { type: Boolean },
    entityType: {},
    entityId: {},
    document: {},
    initialFormState: {}
  },
  emits: ["update:open", "success", "minimize"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t } = useI18n()
const { toast } = useToast()
const documentsStore = useDocumentsStore()
const { setActiveDocument, updateActiveForm, clearActiveDocument } = useMinimizedDocuments()

const isSubmitting = ref(false)
const activeEditor = ref<any>(null)
const showEditorModal = ref(false)

watch(() => showEditorModal.value, (val) => {
  if (!val) {
    setTimeout(() => {
      activeEditor.value = null
      editingBlockIndex.value = null
      localStorage.removeItem('editingDiagramCode')
    }, 300)
  }
})
const editingBlockIndex = ref<number | null>(null)

const existingDiagrams = computed(() => {
  const regex = /```mermaid\n([\s\S]*?)```/g;
  let match;
  const blocks = [];
  let index = 0;
  while ((match = regex.exec(form.value.content)) !== null) {
    const fullMatch = match[0];
    const code = match[1].trim();
    
    // Find the first actual line of code that is not a comment and not empty
    const lines = code.split('\n').map(l => l.trim());
    const firstActualLine = lines.find(l => l && !l.startsWith('%%'));
    const firstWord = firstActualLine ? firstActualLine.split(' ')[0] : '';
    
    let typeId = 'live';
    if (firstWord.includes('classDiagram')) typeId = 'class';
    else if (firstWord.includes('flowchart') || firstWord.includes('graph')) typeId = 'flow';
    else if (firstWord.includes('architecture')) typeId = 'architecture';
    else if (firstWord.includes('gantt')) typeId = 'gantt';
    else if (firstWord.includes('erDiagram')) typeId = 'er';
    else if (firstWord.includes('pie')) typeId = 'pie';
    else if (firstWord.includes('timeline')) typeId = 'timeline';
    else if (firstWord.includes('journey')) typeId = 'journey';
    else if (firstWord.includes('sequenceDiagram')) typeId = 'sequence';
    else if (firstWord.includes('requirementDiagram')) typeId = 'requirement';
    else if (firstWord.includes('xychart')) typeId = 'xychart';
    else if (firstWord.includes('stateDiagram')) typeId = 'state';
    else if (firstWord.includes('kanban')) typeId = 'kanban';
    else if (firstWord.includes('treeView') || firstWord.includes('tree')) typeId = 'tree';
    else if (firstWord.includes('block-beta')) typeId = 'eventmodeling';
    
    const diagramDef = DIAGRAM_TYPES.find(d => d.id === typeId) || DIAGRAM_TYPES.find(d => d.id === 'live');
    
    const titleMatch = code.match(/^%%\s*title:\s*(.+)$/m);
    const title = titleMatch ? titleMatch[1].trim() : '';
    
    blocks.push({
      index: index++,
      raw: fullMatch,
      code: code,
      title: title,
      startIndex: match.index,
      endIndex: match.index + fullMatch.length,
      diagram: diagramDef
    });
  }
  return blocks;
});

const openEditor = (diagramType: any) => {
  editingBlockIndex.value = null;
  localStorage.removeItem('editingDiagramCode');
  activeEditor.value = diagramType
  showEditorModal.value = true
}

const editExistingDiagram = (block: any) => {
  editingBlockIndex.value = block.index;
  localStorage.setItem('editingDiagramCode', block.code);
  activeEditor.value = block.diagram;
  showEditorModal.value = true;
}

const handleDiagramSave = (mermaidCode: string) => {
  // Garantizar que no haya doble bloque de markdown
  let rawCode = mermaidCode.trim();
  const match = rawCode.match(/```mermaid\n([\s\S]*?)```/);
  if (match) {
    rawCode = match[1].trim();
  } else {
    rawCode = rawCode.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '').trim();
  }
  
  if (editingBlockIndex.value !== null) {
    const block = existingDiagrams.value.find(b => b.index === editingBlockIndex.value);
    if (block) {
      // Preservar el título si existía en el bloque anterior: los editores
      // visuales regeneran el código y no conservan la línea `%% title:`
      let finalCode = rawCode;
      const oldTitleMatch = block.code.match(/^%%\s*title:\s*(.+)$/m);
      if (oldTitleMatch) {
        finalCode = finalCode.replace(/^%%\s*title:.*$\n?/m, '');
        finalCode = `%% title: ${oldTitleMatch[1].trim()}\n` + finalCode;
      }

      const formattedCode = '```mermaid\n' + finalCode + '\n```';
      const before = form.value.content.substring(0, block.startIndex);
      const after = form.value.content.substring(block.endIndex);
      form.value.content = before + formattedCode + after;
    }
    editingBlockIndex.value = null;
  } else {
    const formattedCode = '```mermaid\n' + rawCode + '\n```';
    form.value.content += (form.value.content ? '\n\n' : '') + formattedCode;
  }
}
const form = ref({
  title: '',
  type: 'other' as DocumentType,
  content: '',
  isPublic: true,
})

const isEditing = computed(() => !!props.document)

const copyDocumentId = async () => {
  if (!props.document) return
  try {
    await navigator.clipboard.writeText(props.document.id)
    toast({ title: 'ID copiado', description: 'El ID del documento se copió al portapapeles' })
  } catch {
    toast({ title: 'Error', description: 'No se pudo copiar el ID', variant: 'destructive' })
  }
}

const currentDraftKey = computed(() =>
  draftKey(props.entityType, props.entityId, props.document?.id)
)

const documentTypeOptions: { value: DocumentType; label: string }[] = [
  { value: 'scope', label: 'Alcance' },
  { value: 'architecture', label: 'Arquitectura' },
  { value: 'specifications', label: 'Especificaciones' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Otro' },
]

let codeBlockObserver: MutationObserver | null = null;

const injectEditButtonsToDOM = () => {
  const container = document.querySelector('.md-editor-preview');
  if (!container) return;
  
  // Encontrar wrappers de codigo de md-editor-v3
  const codeBlocks = container.querySelectorAll('.md-editor-code-block');
  
  codeBlocks.forEach((block) => {
    // Si ya tiene el boton, ignorar
    if (block.querySelector('.btn-edit-mermaid')) return;
    
    // Buscar la etiqueta de lenguaje
    const langSpan = block.querySelector('.md-editor-code-head > span');
    if (langSpan && langSpan.textContent?.trim() === 'mermaid') {
      const head = block.querySelector('.md-editor-code-head');
      if (head) {
        // Crear boton
        const btn = document.createElement('span');
        btn.className = 'btn-edit-mermaid md-editor-code-action';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-workflow" style="margin-right:4px;"><rect width="8" height="8" x="3" y="3" rx="2"/><path d="M7 11v4a2 2 0 0 0 2 2h4"/><rect width="8" height="8" x="13" y="13" rx="2"/></svg> Editar`;
        btn.style.cursor = 'pointer';
        btn.style.color = 'var(--primary)';
        btn.style.display = 'inline-flex';
        btn.style.alignItems = 'center';
        btn.style.marginLeft = '12px';
        btn.style.fontWeight = 'bold';
        
        btn.onclick = () => {
          const codeEl = block.querySelector('pre code');
          if (codeEl) {
            const rawText = codeEl.textContent?.trim() || '';
            const existing = existingDiagrams.value.find(b => b.code.trim() === rawText);
            if (existing) {
              editExistingDiagram(existing);
            }
          }
        };
        
        head.appendChild(btn);
      }
    }
  });
}

const setupCodeBlockObserver = () => {
  if (codeBlockObserver) codeBlockObserver.disconnect();
  
  const container = document.querySelector('.md-editor');
  if (container) {
    codeBlockObserver = new MutationObserver(() => {
      injectEditButtonsToDOM();
    });
    codeBlockObserver.observe(container, { childList: true, subtree: true });
    injectEditButtonsToDOM();
  }
}

// --- Initialize form when dialog opens ---
// `immediate: true` is critical: when the widget renders this component
// via v-if with open=true, the watch must fire on the initial value.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      if (props.initialFormState) {
        // Restoring from minimized — use the provided state
        form.value = { ...props.initialFormState }
      } else {
        // Check localStorage for a saved draft
        const draft = loadDraft(currentDraftKey.value)
        if (draft) {
          form.value = { ...draft }
        } else if (props.document) {
          form.value = {
            title: props.document.title,
            type: props.document.type,
            content: props.document.content,
            isPublic: props.document.isPublic,
          }
        } else {
          form.value = {
            title: '',
            type: 'other',
            content: '',
            isPublic: true,
          }
        }
      }

      // Track as active document so it survives page reload (after form is set)
      setActiveDocument({
        entityType: props.entityType,
        entityId: props.entityId,
        documentId: props.document?.id ?? null,
        form: { ...form.value },
      })
      
      nextTick(() => setupCodeBlockObserver())
    } else {
      clearActiveDocument()
      if (codeBlockObserver) codeBlockObserver.disconnect()
    }
  },
  { immediate: true }
)

// --- Auto-save draft + active form to localStorage (debounced) ---
let draftTimer: ReturnType<typeof setTimeout> | null = null

watch(
  form,
  (newForm) => {
    if (!props.open) return
    if (draftTimer) clearTimeout(draftTimer)
    draftTimer = setTimeout(() => {
      saveDraft(currentDraftKey.value, { ...newForm })
      updateActiveForm({ ...newForm })
    }, 1000)
  },
  { deep: true }
)

// Flush form state immediately on page unload so nothing is lost
const flushOnUnload = () => {
  if (props.open) {
    saveDraft(currentDraftKey.value, { ...form.value })
    updateActiveForm({ ...form.value })
  }
}
window.addEventListener('beforeunload', flushOnUnload)

onBeforeUnmount(() => {
  if (draftTimer) clearTimeout(draftTimer)
  window.removeEventListener('beforeunload', flushOnUnload)
})

const isValid = computed(() => {
  return form.value.title.trim() !== '' && form.value.content.trim() !== ''
})

const handleSubmit = async () => {
  if (!isValid.value) return

  isSubmitting.value = true

  try {
    if (isEditing.value && props.document) {
      await documentsStore.updateDocument(props.document.id, {
        title: form.value.title,
        type: form.value.type,
        content: form.value.content,
        isPublic: form.value.isPublic,
      })
      toast({
        title: 'Documento actualizado',
        description: form.value.title,
      })
    } else {
      await documentsStore.createDocument({
        entityType: props.entityType,
        entityId: props.entityId,
        title: form.value.title,
        type: form.value.type,
        content: form.value.content,
        isPublic: form.value.isPublic,
      })
      toast({
        title: 'Documento creado',
        description: form.value.title,
      })
    }

    // Clear draft on successful save
    clearDraft(currentDraftKey.value)

    emit('success')
    emit('update:open', false)
  } catch (error) {
    console.error('Error saving document:', error)
    toast({
      title: 'Error al guardar documento',
      description: error instanceof Error ? error.message : 'Ocurrio un error inesperado',
      variant: 'destructive',
    })
  } finally {
    isSubmitting.value = false
  }
}

const handleMinimize = () => {
  // Clear standalone draft — the minimized-documents composable handles its own persistence
  clearDraft(currentDraftKey.value)
  emit('minimize', { ...form.value })
  emit('update:open', false)
}

const handleClose = () => {
  emit('update:open', false)
}

const handleEscapeOrOutside = (event: Event) => {
  event.preventDefault()
  handleMinimize()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Dialog), {
      open: __props.open,
      "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('update:open', $event)))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogContent), {
          class: "w-screen h-screen max-w-none max-h-none m-0 rounded-none flex flex-col [&>button.absolute]:hidden",
          onEscapeKeyDown: handleEscapeOrOutside,
          onPointerDownOutside: handleEscapeOrOutside
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", null, [
                _createVNode(_unref(DialogTitle), { class: "text-xl" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isEditing.value ? 'Editar Documento' : 'Nuevo Documento'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DialogDescription), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isEditing.value ? 'Modifica el documento. Se incrementara la version automaticamente.' : 'Crea un nuevo documento en formato Markdown.'), 1)
                  ]),
                  _: 1
                }),
                (isEditing.value && __props.document)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createElementVNode("span", _hoisted_3, _toDisplayString(__props.document.id), 1),
                      _createElementVNode("button", {
                        class: "inline-flex items-center justify-center h-4 w-4 rounded hover:bg-muted transition-colors",
                        title: "Copiar ID",
                        onClick: copyDocumentId
                      }, [
                        _createVNode(_unref(Copy), { class: "h-2.5 w-2.5" })
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_unref(Button), {
                  variant: "outline",
                  onClick: handleClose,
                  disabled: isSubmitting.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('common.cancel')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(_unref(Button), {
                  variant: "outline",
                  onClick: handleMinimize,
                  disabled: isSubmitting.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Minus), { class: "mr-2 h-4 w-4" }),
                    _cache[6] || (_cache[6] = _createTextVNode(" Minimizar ", -1))
                  ]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(_unref(Button), {
                  onClick: handleSubmit,
                  disabled: isSubmitting.value || !isValid.value
                }, {
                  default: _withCtx(() => [
                    (isSubmitting.value)
                      ? (_openBlock(), _createBlock(_unref(Loader2), {
                          key: 0,
                          class: "mr-2 h-4 w-4 animate-spin"
                        }))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(isEditing.value ? _unref(t)('common.save') : _unref(t)('common.create')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_unref(Label), { for: "title" }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("Titulo *", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_unref(Input), {
                    id: "title",
                    modelValue: form.value.title,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.title) = $event)),
                    placeholder: "Nombre del documento",
                    disabled: isSubmitting.value,
                    class: "text-lg"
                  }, null, 8, ["modelValue", "disabled"])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(Label), null, {
                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                      _createTextVNode("Tipo", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_unref(Select), {
                    modelValue: form.value.type,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.type) = $event)),
                    disabled: isSubmitting.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SelectTrigger), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SelectValue))
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(SelectContent), null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(documentTypeOptions, (option) => {
                            return _createVNode(_unref(SelectItem), {
                              key: option.value,
                              value: option.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(option.label), 1)
                              ]),
                              _: 2
                            }, 1032, ["value"])
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["modelValue", "disabled"])
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(_unref(Switch), {
                    id: "isPublic",
                    checked: form.value.isPublic,
                    "onUpdate:checked": _cache[2] || (_cache[2] = ($event: any) => (form.value.isPublic = $event)),
                    disabled: isSubmitting.value
                  }, null, 8, ["checked", "disabled"]),
                  _createVNode(_unref(Label), {
                    for: "isPublic",
                    class: "font-normal cursor-pointer whitespace-nowrap"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(form.value.isPublic ? 'Publico' : 'Interno'), 1)
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_unref(DropdownMenu), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Button), {
                          variant: "outline",
                          disabled: isSubmitting.value
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Workflow), { class: "w-4 h-4 mr-2" }),
                            _cache[9] || (_cache[9] = _createTextVNode(" Añadir Diagrama ", -1))
                          ]),
                          _: 1
                        }, 8, ["disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(DropdownMenuContent), null, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DIAGRAM_TYPES), (diagram) => {
                          return (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                            key: diagram.id,
                            onClick: ($event: any) => (openEditor(diagram))
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(), _createBlock(_resolveDynamicComponent(diagram.icon), { class: "w-4 h-4 mr-2 text-primary" })),
                              _createTextVNode(" " + _toDisplayString(diagram.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["onClick"]))
                        }), 128))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (existingDiagrams.value.length > 0)
                  ? (_openBlock(), _createBlock(_unref(DropdownMenu), { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Button), {
                              variant: "outline",
                              class: "border-primary text-primary",
                              disabled: isSubmitting.value
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Workflow), { class: "w-4 h-4 mr-2" }),
                                _createTextVNode(" Editar Diagrama (" + _toDisplayString(existingDiagrams.value.length) + ") ", 1)
                              ]),
                              _: 1
                            }, 8, ["disabled"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_unref(DropdownMenuContent), null, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(existingDiagrams.value, (block) => {
                              return (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                                key: block.index,
                                onClick: ($event: any) => (editExistingDiagram(block))
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(), _createBlock(_resolveDynamicComponent(block.diagram.icon), { class: "w-4 h-4 mr-2 text-primary" })),
                                  _createTextVNode(" " + _toDisplayString(block.title ? block.title : `Editar ${block.diagram.label} #${block.index + 1}`), 1)
                                ]),
                                _: 2
                              }, 1032, ["onClick"]))
                            }), 128))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(MdEditor), {
                  modelValue: form.value.content,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.content) = $event)),
                  language: "en-US",
                  preview: true,
                  toolbarsExclude: ['github', 'save'],
                  disabled: isSubmitting.value,
                  class: "h-full",
                  style: {"height":"100%"}
                }, null, 8, ["modelValue", "disabled"])
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"]),
    (activeEditor.value)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(activeEditor.value.component), {
          key: 0,
          open: showEditorModal.value,
          "onUpdate:open": _cache[5] || (_cache[5] = ($event: any) => ((showEditorModal).value = $event)),
          onSave: handleDiagramSave
        }, null, 40, ["open"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})